import { useCallback, useMemo } from 'react';
import { WithAccountId } from '@mews-commander/types';
import { LocalizedText, useFeatureFlag } from '@mews/framework';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { TableHeader, useTableState } from '@optimus-web/core';
import { useGetAccountingConfigurationReceivableTrackingEnabled, useGetPaymentTableRecords } from '../../../queries';
import { PAYMENTS_LIMIT, PAYMENTS_TABLE_COLUMNS } from '../../../constants';
import { TableDivider } from '../TableDivider';
import { PaymentTablesColumns } from '../types';
import { PaymentTable } from '../PaymentTable';
import { ProcessPaymentDialogButton } from '../../forms/ProcessPayment/ProcessPaymentDialogButton';
import { ProcessPaymentType } from '../../forms/ProcessPayment/ProcessPaymentType';
import { getPaymentsColumns, PaymentTableRecordExtended } from './PaymentsTableMapping';
import { getPaymentsTableStates, prepareTableData } from './utils';
import { useGetTableRecordsTotalAmount, useOpenCashierDetailModal, useOpenReservationDetailModal } from './hooks';

interface Props extends WithAccountId {
    isPaymentAttemptsTable?: boolean;
    addExceededBanner: () => void;
    hideDivider?: boolean;
    enabledColumns?: PaymentTablesColumns[];
    hideTableHeader?: boolean;
    isCompany?: boolean;
}

export const PaymentsTable = ({
    accountId,
    addExceededBanner,
    isPaymentAttemptsTable = false,
    hideDivider = false,
    enabledColumns = PAYMENTS_TABLE_COLUMNS,
    hideTableHeader = false,
    isCompany = false,
}: Props) => {
    const isReservationLinkToPaymentsEnabled = useFeatureFlag(FeatureFlag.reservationLinkToPaymentsEnabled);
    const unlinkingPaymentsFromReservationEnabled = useFeatureFlag(
        FeatureFlag.unlinkingPaymentsOnReservationCancellation
    );
    const displayReservationLink = isReservationLinkToPaymentsEnabled || unlinkingPaymentsFromReservationEnabled;
    const isCompanyTerminalPaymentsEnabled = useFeatureFlag(FeatureFlag.companyTerminalPaymentsEnabled);

    const {
        data: tableData,
        isFetching: isTableDataFetching,
        isError: isTableDateError,
        refetch,
    } = useGetPaymentTableRecords({
        accountId,
        states: getPaymentsTableStates(isPaymentAttemptsTable),
    });
    const {
        data: receivableTrackingEnabled = false,
        isFetching: isReceivableTrackingFetching,
        isError: isReceivableTrackingError,
    } = useGetAccountingConfigurationReceivableTrackingEnabled();

    const [tableState, setTableState] = useTableState<PaymentTableRecordExtended>({
        pagination: {
            show: false,
        },
    });

    const refetchTableData = useCallback(async () => refetch(), [refetch]);
    const openCashierDetailModal = useOpenCashierDetailModal();
    const openReservationDetailModal = useOpenReservationDetailModal();

    if (tableData && tableData?.length >= PAYMENTS_LIMIT) {
        addExceededBanner();
    }

    const source = useMemo(
        () => (tableData ? prepareTableData(tableData, isPaymentAttemptsTable) : []),
        [tableData, isPaymentAttemptsTable]
    );
    const totalAmount = useGetTableRecordsTotalAmount(source);

    const actionButtons = !isPaymentAttemptsTable ? (
        <ProcessPaymentDialogButton
            buttonIcon
            accountId={accountId}
            allowedPaymentTypes={
                isCompany
                    ? [
                          ProcessPaymentType.CashPayment,
                          ProcessPaymentType.ExternalPayment,
                          ProcessPaymentType.GatewayPayment,
                          ProcessPaymentType.ExternalCardPayment,
                          ...(isCompanyTerminalPaymentsEnabled ? [ProcessPaymentType.MewsTerminalPayment] : []),
                      ]
                    : [
                          ProcessPaymentType.GatewayPayment,
                          ProcessPaymentType.MewsTerminalPayment,
                          ProcessPaymentType.ExternalCardPayment,
                          ProcessPaymentType.CashPayment,
                          ProcessPaymentType.ExternalPayment,
                      ]
            }
        />
    ) : null;

    const columns = useMemo(
        () =>
            getPaymentsColumns({
                ownerId: accountId,
                openCashierDetailModal,
                openReservationDetailModal,
                refetchTableData,
                isPaymentAttemptsTable,
                displayReservationLink,
                receivableTrackingEnabled,
                isCompany,
            }).filter(({ id }) => enabledColumns.includes(id as PaymentTablesColumns)),
        [
            accountId,
            enabledColumns,
            isPaymentAttemptsTable,
            displayReservationLink,
            openCashierDetailModal,
            openReservationDetailModal,
            refetchTableData,
            receivableTrackingEnabled,
            isCompany,
        ]
    );
    return (
        <>
            {!hideTableHeader && (
                <TableHeader
                    title={
                        <LocalizedText
                            textKey={isPaymentAttemptsTable ? TextKey.PaymentAttempts : TextKey.PaymentPlural}
                        />
                    }
                    rightSide={actionButtons}
                />
            )}
            <PaymentTable
                columns={columns}
                data={[
                    ...source,
                    ...(!isPaymentAttemptsTable
                        ? [{ amount: totalAmount, isTotalAmount: true } as PaymentTableRecordExtended]
                        : []),
                ]}
                uniqueRowId="id"
                state={tableState}
                onStateChange={setTableState}
                amountOfRows={source.length}
                isError={isTableDateError || isReceivableTrackingError}
                onTryAgain={async () => refetchTableData()}
                loading={isTableDataFetching || isReceivableTrackingFetching}
            />
            {!hideDivider && <TableDivider />}
        </>
    );
};
